import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import * as React from 'react'
import { LoaderIcon } from '@/app/features/icons/Loader'
import DriverContractsTable from '../components/tables/ContractsDetails/DriverContractsDetailsTable'
import DriverCardsTable from '../components/tables/Cards/DriverCardsTable'
import DriverBasicInfo from '../components/DriverBasciInfo'
import { TableProvider } from '@/modules/drivers/context/TableContext'
import useGroupService from '@/modules/drivers/services/GroupService'
import { DriverFormProvider } from '@/modules/drivers/context/DriverFormContext'
import AddDriverModal from '@/modules/drivers/modals/driverAdd/AddDriverModal'
import { useQueryDriver } from '@/lib/api/company/drivers'
import axios from 'axios'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const ContractIndividual = ({
  driverId,
  companyId,
}: {
  driverId: string
  companyId: string
}) => {
  const loading = useLoadingStore(selectLoading)
  const { t } = useLocalize()
  const { data: driver = null, isLoading: isDriverLoading, error: driverError } = useQueryDriver({
    companyId,
    driverId,
    withPersonalData: true,
  })
  const accessDenied = axios.isAxiosError(driverError) && driverError.response?.status === 403
  const { getGroups } = useGroupService()
  const [groupNames, setGroupNames] = React.useState<string[]>([])
  const [isOpenEditDriverModal, setIsOpenEditDriverModal] =
    React.useState(false)

  React.useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groups = await getGroups({ companyId })

        const driverGroupIds = driver?.groups || []
        const names = groups
          .filter((group) => driverGroupIds.includes(group.id))
          .map((group) => group.name)

        setGroupNames(names.length ? names : ['-'])
      } catch (error) {
        console.error('Error fetching groups:', error)
        setGroupNames(['-'])
      }
    }

    if (driver?.groups && companyId) {
      fetchGroups()
    }
  }, [driver?.groups, companyId])

  return (
    <section className="tw-flex tw-h-fit tw-min-h-full  tw-w-full tw-flex-col">
      {accessDenied ? (
        <p role="alert" className="tw-m-4 tw-rounded-md tw-bg-error/10 tw-px-3 tw-py-2 tw-text-sm tw-text-error">
          {t('driverModule.no-access-to-driver')}
        </p>
      ) : loading || isDriverLoading || !driver ? (
        <div className="tw-grid tw-flex-1 tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : (
        <>
          <DriverBasicInfo
            driver={driver}
            groupNames={groupNames}
            companyId={companyId}
            setIsOpenEditDriverModal={setIsOpenEditDriverModal}
          />
        </>
      )}
      {driver && (
        <>
          <TableProvider bulk={false}>
            <DriverCardsTable
              cards={driver?.driverCards ?? []}
              companyId={companyId}
              driverId={driver?.driverId?.value}
            />
          </TableProvider>
          <TableProvider bulk={false}>
            <DriverContractsTable
              driverId={driver?.driverId?.value}
              companyId={companyId}
              driverName={driver?.fullName}
            />
          </TableProvider>
        </>
      )}
      {isOpenEditDriverModal && (
        <DriverFormProvider
          companyId={companyId}
          isEdit={true}
          driverId={driver?.driverId?.value || null}
        >
          <AddDriverModal
            companyId={companyId}
            isOpen={isOpenEditDriverModal}
            close={() => setIsOpenEditDriverModal(false)}
          />
        </DriverFormProvider>
      )}
    </section>
  )
}

export default ContractIndividual
